import { useState, useMemo } from 'react';
import { FileText, DollarSign, Search } from 'lucide-react';
import { useReactTable, getCoreRowModel, type ColumnDef } from '@tanstack/react-table';
import { DataTable } from '@/components/data-table/data-table';
import { DataTablePagination } from '@/components/data-table/data-table-pagination';
import { PageHeader } from '@/components/shared/page-header';
import { useCreditAccountsAging, type CreditAccountSummary } from '@/hooks/customer-engagement/use-credit-accounts';
import { formatCurrency, formatDateTime } from '@/lib/utils';
import { apiErrorMessage } from '@/lib/api-error';
import { usePermissions } from '@/hooks/use-permissions';
import { CreditStatementPage } from './credit-statement-page';
import { RecordPaymentDialog } from './credit-record-payment-dialog';

const PER_PAGE_OPTIONS = [10, 25, 50, 100];

// Aged-receivable buckets. `field` is the per-customer amount column (and also
// the aggregate summary-card key — both share these names), `key` is the value
// used by the filter control. Kept in one place so the cards, the dropdown, and
// the row filter all stay in sync.
type BucketKey = '0-30' | '31-60' | '61-90' | '90+';
type BucketField = 'bucket_0_30' | 'bucket_31_60' | 'bucket_61_90' | 'bucket_90_plus';
const AGING_BUCKETS: {
  key: BucketKey;
  label: string;
  field: BucketField;
  tone: 'green' | 'yellow' | 'orange' | 'red';
}[] = [
  { key: '0-30', label: '0–30 days', field: 'bucket_0_30', tone: 'green' },
  { key: '31-60', label: '31–60 days', field: 'bucket_31_60', tone: 'yellow' },
  { key: '61-90', label: '61–90 days', field: 'bucket_61_90', tone: 'orange' },
  { key: '90+', label: '90+ days', field: 'bucket_90_plus', tone: 'red' },
];

interface Props {
  title: string;
  description: string;
  onBack: () => void;
}

export function PosCreditAccountsTab({ title, description, onBack }: Props) {
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(25);
  const [search, setSearch] = useState('');
  const [bucketFilter, setBucketFilter] = useState<BucketKey | null>(null);
  const [statementFor, setStatementFor] = useState<CreditAccountSummary | null>(null);
  const [paymentFor, setPaymentFor] = useState<CreditAccountSummary | null>(null);
  const canRecordPayment = usePermissions().has('reports.pos.credit');

  const { data, isLoading, isError, error } = useCreditAccountsAging();

  const allRows = data?.data ?? [];
  const buckets = data?.meta.buckets;

  // Filter by customer name/phone (digits-only phone match so formatting is
  // ignored) and by aged-receivable bucket (accounts with a charge in the
  // selected age bucket).
  const rows = useMemo(() => {
    const q = search.trim().toLowerCase();
    const qDigits = q.replace(/\D/g, '');
    const bucketField = bucketFilter ? AGING_BUCKETS.find((b) => b.key === bucketFilter)?.field : null;
    return allRows.filter((r) => {
      if (q) {
        const nameMatch = r.customerName?.toLowerCase().includes(q);
        const phoneDigits = (r.customerPhone ?? '').replace(/\D/g, '');
        const phoneMatch = qDigits.length > 0 && phoneDigits.includes(qDigits);
        if (!nameMatch && !phoneMatch) return false;
      }
      if (bucketField && Number(r[bucketField]) <= 0) return false;
      return true;
    });
  }, [allRows, search, bucketFilter]);

  const selectBucket = (key: BucketKey | null) => {
    setBucketFilter(key);
    setPage(1);
  };

  const total = rows.length;
  const totalPages = Math.max(1, Math.ceil(total / perPage));
  const pageRows = useMemo(() => rows.slice((page - 1) * perPage, page * perPage), [rows, page, perPage]);

  const handlePerPageChange = (newPerPage: number) => {
    setPerPage(newPerPage);
    setPage(1);
  };

  const columns: ColumnDef<CreditAccountSummary>[] = [
    {
      accessorKey: 'customerName',
      header: 'Customer',
      cell: ({ row }) => (
        <div>
          <div className="font-medium text-foreground">{row.original.customerName}</div>
          <div className="text-xs text-muted-foreground">{row.original.customerPhone}</div>
        </div>
      ),
    },
    {
      accessorKey: 'customerType',
      header: 'Type',
      cell: ({ row }) => <TypeBadge type={row.original.customerType} />,
    },
    {
      accessorKey: 'creditLimit',
      header: () => <div className="text-right">Credit limit</div>,
      cell: ({ row }) => (
        <div className="text-right whitespace-nowrap">{formatCurrency(Number(row.original.creditLimit))}</div>
      ),
    },
    {
      accessorKey: 'currentBalance',
      header: () => <div className="text-right">Balance</div>,
      cell: ({ row }) => (
        <div className="text-right font-medium text-foreground whitespace-nowrap">
          {formatCurrency(Number(row.original.currentBalance))}
        </div>
      ),
    },
    {
      accessorKey: 'pastDueAmount',
      header: () => <div className="text-right">Past due</div>,
      cell: ({ row }) => {
        const v = Number(row.original.pastDueAmount);
        return (
          <div
            className={`text-right whitespace-nowrap ${v > 0 ? 'text-red-700 font-medium' : 'text-muted-foreground'}`}
          >
            {v > 0 ? formatCurrency(v) : '—'}
          </div>
        );
      },
    },
    {
      accessorKey: 'totalCharged',
      header: () => <div className="text-right">Charged</div>,
      cell: ({ row }) => (
        <div className="text-right whitespace-nowrap">{formatCurrency(Number(row.original.totalCharged))}</div>
      ),
    },
    {
      accessorKey: 'totalPaid',
      header: () => <div className="text-right">Paid</div>,
      cell: ({ row }) => (
        <div className="text-right whitespace-nowrap">{formatCurrency(Number(row.original.totalPaid))}</div>
      ),
    },
    {
      accessorKey: 'oldestChargeAt',
      header: 'Oldest charge',
      cell: ({ row }) => (
        <span className="text-xs text-muted-foreground whitespace-nowrap">
          {row.original.oldestChargeAt ? formatDateTime(row.original.oldestChargeAt) : '—'}
        </span>
      ),
    },
    {
      accessorKey: 'lastPaymentAt',
      header: 'Last payment',
      cell: ({ row }) => (
        <span className="text-xs text-muted-foreground whitespace-nowrap">
          {row.original.lastPaymentAt ? formatDateTime(row.original.lastPaymentAt) : '—'}
        </span>
      ),
    },
    {
      id: 'actions',
      header: () => <div className="text-right">Actions</div>,
      cell: ({ row }) => (
        <div className="flex items-center justify-end gap-1 whitespace-nowrap">
          <button
            type="button"
            onClick={() => setStatementFor(row.original)}
            className="inline-flex items-center gap-1 px-2 py-1 text-xs text-gray-700 hover:bg-gray-100 rounded"
            title="View statement"
          >
            <FileText className="h-3.5 w-3.5" /> Statement
          </button>
          {canRecordPayment && (
            <button
              type="button"
              onClick={() => setPaymentFor(row.original)}
              className="inline-flex items-center gap-1 px-2 py-1 text-xs text-blue-700 hover:bg-blue-50 rounded"
              title="Record payment"
            >
              <DollarSign className="h-3.5 w-3.5" /> Record payment
            </button>
          )}
        </div>
      ),
    },
  ];

  const table = useReactTable({
    data: pageRows,
    columns,
    getCoreRowModel: getCoreRowModel(),
  });

  // Viewing a customer's statement takes over the whole tab as a full page
  // (with its own back button) rather than a cramped modal.
  if (statementFor) {
    return <CreditStatementPage customer={statementFor} onBack={() => setStatementFor(null)} />;
  }

  return (
    <div className="space-y-4">
      <PageHeader title={title} description={description} onBack={onBack} />

      {/* Aging buckets across all credit/wholesale customers — click a card to
          filter the table to accounts with receivables in that age bucket. */}
      {buckets && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {AGING_BUCKETS.map((b) => (
            <BucketCard
              key={b.key}
              label={b.label}
              value={buckets[b.field]}
              tone={b.tone}
              selected={bucketFilter === b.key}
              onClick={() => selectBucket(bucketFilter === b.key ? null : b.key)}
            />
          ))}
        </div>
      )}

      {isError && (
        <div className="rounded-md bg-red-50 border border-red-200 p-3 text-sm text-red-700">
          Failed to load: {apiErrorMessage(error)}
        </div>
      )}

      {isLoading ? (
        <div className="flex h-64 items-center justify-center text-muted-foreground">Loading...</div>
      ) : (
        !isError && (
          <>
            <div className="flex flex-col gap-3 sm:flex-row sm:items-start">
              <div className="w-full min-w-0 sm:max-w-md">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                  <input
                    type="search"
                    placeholder="Search by customer name or phone..."
                    value={search}
                    onChange={(e) => {
                      setSearch(e.target.value);
                      setPage(1);
                    }}
                    className="h-9 w-full rounded-lg border border-input bg-background pl-9 pr-4 text-sm outline-none focus:border-primary focus:ring-1 focus:ring-primary"
                  />
                </div>
                <p className="mt-1 text-xs text-muted-foreground">Search works by customer name or phone number.</p>
              </div>

              {/* Aged-receivable filter — mirrors the clickable bucket cards. */}
              <div className="w-full min-w-0 sm:w-56">
                <label htmlFor="aging-bucket-filter" className="sr-only">
                  Aging bucket
                </label>
                <select
                  id="aging-bucket-filter"
                  value={bucketFilter ?? ''}
                  onChange={(e) => selectBucket(e.target.value ? (e.target.value as BucketKey) : null)}
                  className="h-9 w-full rounded-lg border border-input bg-background px-3 text-sm outline-none focus:border-primary focus:ring-1 focus:ring-primary"
                >
                  <option value="">All aging buckets</option>
                  {AGING_BUCKETS.map((b) => (
                    <option key={b.key} value={b.key}>
                      Aged {b.label}
                    </option>
                  ))}
                </select>
                <p className="mt-1 text-xs text-muted-foreground">
                  {bucketFilter ? (
                    <button type="button" onClick={() => selectBucket(null)} className="text-primary hover:underline">
                      Clear aging filter
                    </button>
                  ) : (
                    'Filter accounts by receivable age.'
                  )}
                </p>
              </div>
            </div>
            <DataTable table={table} columns={columns} />
            <DataTablePagination
              page={page}
              totalPages={totalPages}
              total={total}
              perPage={perPage}
              onPageChange={setPage}
              perPageOptions={PER_PAGE_OPTIONS}
              onPerPageChange={handlePerPageChange}
            />
          </>
        )
      )}

      <RecordPaymentDialog
        open={canRecordPayment && !!paymentFor}
        customer={paymentFor}
        onClose={() => setPaymentFor(null)}
      />
    </div>
  );
}

function TypeBadge({ type }: { type: CreditAccountSummary['customerType'] }) {
  const tone =
    type === 'credit'
      ? 'bg-amber-100 text-amber-800'
      : type === 'wholesale'
        ? 'bg-purple-100 text-purple-800'
        : 'bg-gray-100 text-gray-700';
  return <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium ${tone}`}>{type}</span>;
}

function BucketCard({
  label,
  value,
  tone,
  selected,
  onClick,
}: {
  label: string;
  value: string;
  tone: 'green' | 'yellow' | 'orange' | 'red';
  selected?: boolean;
  onClick?: () => void;
}) {
  const toneClass = {
    green: 'bg-green-50 border-green-200 text-green-900',
    yellow: 'bg-yellow-50 border-yellow-200 text-yellow-900',
    orange: 'bg-orange-50 border-orange-200 text-orange-900',
    red: 'bg-red-50 border-red-200 text-red-900',
  }[tone];
  const ringClass = {
    green: 'ring-green-500',
    yellow: 'ring-yellow-500',
    orange: 'ring-orange-500',
    red: 'ring-red-500',
  }[tone];
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={selected}
      className={`rounded-md border p-3 text-left transition-shadow hover:shadow-sm focus:outline-none focus-visible:ring-2 ${ringClass} ${toneClass} ${
        selected ? `ring-2 ${ringClass}` : ''
      }`}
    >
      <div className="text-xs uppercase tracking-wide opacity-80">{label}</div>
      <div className="text-2xl font-semibold mt-1">{formatCurrency(Number(value))}</div>
    </button>
  );
}
